import merge from 'lodash/merge'
import { useMemo, useRef } from 'react'
import { View } from 'react-native'
import Slider from 'react-slick'
import 'slick-carousel/slick/slick-theme.css'
import 'slick-carousel/slick/slick.css'
import { match } from 'ts-pattern'

import { BlockContent } from '@/components/renderers/BlockContent'
import { EVENTS } from '@/constants/analytics'
import { useAnalytics } from '@/context/AnalyticsContext'
import type { PortableTextTypeComponentProps } from '@portabletext/react'

import { CustomButtons } from './components'
import './custom.css'
import { carouselSchema } from './schema'
import { carouselStyles } from './styles'

interface CarouselProps {
  node: PortableTextTypeComponentProps<unknown>
  width: number
  variant?: 'default' | 'products'
}

export const Carousel = (props: CarouselProps) => {
  const carousel = useRef<Slider>(null)

  const baseStyles = carouselStyles({
    variant: props.variant,
  })

  const value = useMemo(() => carouselSchema.parse(props.node.value), [props.node])

  const { slides } = value
  const { track } = useAnalytics()

  const type = value.displayType ?? 'single'

  const handleSwipeEvent = (currendSlide: number) => {
    track(EVENTS.CarouselSwiped, {
      slide: currendSlide,
      variant: props.variant,
    })
  }

  const settings = useMemo(() => {
    const defaultSettings = {
      dots: false,
      arrows: false,
      speed: 500,
      slidesToShow: 1,
      rows: 1,
      slidesToScroll: 1,
    }

    return match(type)
      .with('multiple', () => {
        return merge(defaultSettings, {
          responsive: [
            {
              breakpoint: 3840,
              settings: {
                slidesToShow: 3,
                slidesToScroll: 2,
              },
            },
            {
              breakpoint: 1440,
              settings: {
                slidesToShow: 2,
                slidesToScroll: 2,
              },
            },
            {
              breakpoint: 1024,
              settings: {
                slidesToShow: 2,
                slidesToScroll: 2,
                initialSlide: 2,
              },
            },
            {
              breakpoint: 464,
              settings: {
                slidesToShow: 1,
                slidesToScroll: 1,
              },
            },
          ],
        })
      })
      .otherwise(() => {
        return merge(defaultSettings, {
          slidesToShow: 1,
          slidesToScroll: 1,
        })
      })
  }, [type])

  const shouldApplyMaxWidth = !value.infinite

  if (!slides) {
    return null
  }

  return (
    <View
      className={baseStyles.container()}
      style={shouldApplyMaxWidth ? { maxWidth: value.maxWidth ?? props.width } : undefined}
    >
      <Slider
        ref={carousel}
        className={baseStyles.itemContainer()}
        infinite={value.infinite ?? true}
        variableWidth={value.variableWidth ?? true}
        afterChange={handleSwipeEvent}
        {...settings}
      >
        {slides.map((slide) => (
          <BlockContent
            width={props.width}
            blocks={slide.content}
            sectionName={value.name ?? 'carousel'}
            key={slide._key}
            space="none"
          />
        ))}
      </Slider>
      <CustomButtons
        track={track}
        arrowsHorizontalAlignment={value.arrowsHorizontalAlignment}
        next={() => carousel.current?.slickNext()}
        previous={() => carousel.current?.slickPrev()}
      />
    </View>
  )
}
